import { clsx } from "clsx";
import { twMerge } from "tailwind-merge"

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Resolve a file URL — returns absolute URLs as-is, prepends API_BASE to relative paths.
 */
export function fileUrl(url) {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${API_BASE}${url}`;
}


export function formatCurrency(amount, _currency = 'NGN') {
  return '₦' + new Intl.NumberFormat('en-NG', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);
}

// Compact currency format for mobile (e.g., ₦1.7M instead of ₦1,736,449.48)
export function formatCurrencyCompact(amount, _currency = 'NGN') {
  if (amount >= 1000000000) {
    return '₦' + (amount / 1000000000).toFixed(1) + 'B';
  } else if (amount >= 1000000) {
    return '₦' + (amount / 1000000).toFixed(1) + 'M';
  } else if (amount >= 1000) {
    return '₦' + (amount / 1000).toFixed(1) + 'K';
  }
  return '₦' + amount.toFixed(0);
}

export function formatDate(date, includeTime = true, tz = null) {
  if (!date) return '-';
  const parsed = new Date(date);
  if (isNaN(parsed.getTime())) return '-';
  // Use effective timezone from localStorage if not explicitly provided
  const effectiveTz = tz || (typeof localStorage !== 'undefined' && localStorage.getItem('bbc_effective_timezone')) || undefined;
  // Project standard: dd/mm/yyyy (en-GB, numeric) + 24h time when requested.
  const opts = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    ...(effectiveTz && { timeZone: effectiveTz }),
  };
  if (includeTime) {
    opts.hour = '2-digit';
    opts.minute = '2-digit';
    opts.hour12 = false;
    return parsed.toLocaleString('en-GB', opts);
  }
  return parsed.toLocaleDateString('en-GB', opts);
}

export function formatDateTime(date, tz = null) {
  if (!date) return '-';
  const parsed = new Date(date);
  if (isNaN(parsed.getTime())) return '-';
  const effectiveTz = tz || (typeof localStorage !== 'undefined' && localStorage.getItem('bbc_effective_timezone')) || undefined;
  return parsed.toLocaleString('en-GB', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    ...(effectiveTz && { timeZone: effectiveTz }),
  });
}

/**
 * Resolve a repayment start day value to a numeric day for a given year/month.
 * Handles "last" (last day of month), numeric strings, and numbers.
 */
export function resolveRepaymentDay(dayValue, year, month) {
  if (dayValue === 'last' || dayValue === 'Last') {
    // Day 0 of the next month = last day of current month
    return new Date(year, month + 1, 0).getDate();
  }
  const num = parseInt(dayValue, 10);
  if (isNaN(num) || num < 1) return 1;
  // Clamp to the actual last day of the month
  const lastDay = new Date(year, month + 1, 0).getDate();
  return Math.min(num, lastDay);
}

export function getLoanTypeLabel(type, productName, reason) {
  // Admin-created loans display as "Admin Loan (reason)".
  if (type === 'admin_loan') {
    const r = (reason || '').toString().trim();
    return r ? `Admin Loan (${r})` : 'Admin Loan';
  }
  if (productName) return productName;
  const labels = {
    personal: 'Personal Loan',
    personal_flat: 'Personal Loan (Flat Rate)',
    personal_reducing: 'Personal Loan (Reducing Balance)',
    top_up: 'Top-up Loan',
    top_up_flat: 'Top-up Loan (Flat Rate)',
    top_up_reducing: 'Top-up Loan (Reducing Balance)',
    top_up_repayment: 'Top-up Loan (Existing Repayment)',
    business_sme: 'Business/SME Loan',
    business_sme_flat: 'Business/SME Loan (Flat Rate)',
    business_sme_reducing: 'Business/SME Loan (Reducing Balance)',
    group: 'Group Loan',
    group_flat: 'Group Loan (Flat Rate)',
    group_reducing: 'Group Loan (Reducing Balance)',
    migration_topup_amount: 'Migration Top-Up (Loan Amount)',
    migration_topup_repayment: 'Migration Top-Up (Repayment)'
  };
  return labels[type] || type?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || type;
}

export function getStatusColor(status) {
  const colors = {
    pending: 'bg-yellow-100 text-yellow-800',
    under_review: 'bg-blue-100 text-blue-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
    disbursed: 'bg-purple-100 text-purple-800',
    active: 'bg-teal-100 text-teal-800',
    completed: 'bg-gray-100 text-gray-800',
    defaulted: 'bg-red-200 text-red-900',
    written_off: 'bg-red-50 text-red-800 border border-red-300',
    paid_off: 'bg-gray-100 text-gray-800',
    liquidated: 'bg-orange-100 text-orange-800',
    draft: 'bg-gray-100 text-gray-600',
    awaiting_guarantor: 'bg-orange-100 text-orange-800'
  };
  return colors[status] || 'bg-gray-100 text-gray-800';
}

export function getStatusLabel(status) {
  const labels = {
    pending: 'Pending',
    under_review: 'Under Review',
    approved: 'Approved',
    rejected: 'Declined',
    disbursed: 'Disbursed',
    active: 'Active',
    completed: 'Completed',
    defaulted: 'Defaulted',
    written_off: 'Written Off',
    paid_off: 'Paid Off',
    liquidated: 'Liquidated',
    draft: 'Draft',
    awaiting_guarantor: 'Awaiting Guarantor'
  };
  return labels[status] || status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || status;
}

// Selectable payment-mode options for filters (value + label)
export const PAYMENT_MODE_OPTIONS = [
  { value: 'cash', label: 'Cash' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'ippis_deduction', label: 'IPPIS Deduction' },
  { value: 'salary_deduction', label: 'Salary Deduction' },
  { value: 'mobile_money', label: 'Mobile Money' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'card', label: 'Card' },
  { value: 'overpayment_transfer', label: 'Overpayment' },
  { value: 'others', label: 'Others' },
];

// Payment mode / method → human label (Cash, Bank Transfer, IPPIS Deduction, etc.)
export function formatPaymentMode(mode) {
  if (!mode) return '—';
  const labels = {
    cash: 'Cash',
    bank_transfer: 'Bank Transfer',
    ippis_deduction: 'IPPIS Deduction',
    salary_deduction: 'Salary Deduction',
    mobile_money: 'Mobile Money',
    cheque: 'Cheque',
    check: 'Cheque',
    card: 'Card',
    stripe: 'Card (Stripe)',
    enaira: 'eNaira',
    overpayment_transfer: 'Overpayment',
    loan_deduction: 'Loan Deduction',
    direct: 'Direct',
    others: 'Others',
    other: 'Others',
  };
  return labels[mode] || mode.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

export function getRoleLabel(role) {
  const labels = {
    admin: 'Administrator',
    regional_manager: 'Regional Manager',
    branch_manager: 'Branch Manager',
    loan_officer: 'Loan Officer',
    cashier: 'Cashier',
    customer: 'Customer'
  };
  return labels[role] || role;
}
